import { I18n } from 'react-redux-i18n';

import { components } from 'apiTyped/types';
import { dataFilter } from 'modules/Prices/helpers';

const getDataFilters = ({
  wine,
  vintage,
  format,
  network
}: {
  wine: string | null;
  vintage: string | null;
  format: string | null;
  network: string | null;
}) => [
  { value: wine, key: 'wine_name', default: [] },
  { value: vintage, key: 'vintage', default: [] },
  { value: format, key: 'bottle_format' },
  { value: network, key: 'network_code', default: [] }
];

const filterDataByMarket = ({
  data,
  markets
}: {
  data: components['schemas']['DataDetail'][];
  markets: string[];
}) => {
  return markets && markets.length > 0
    ? data.filter((item) => markets.includes(item.market_name))
    : [];
};

const getChartData = ({
  data,
  wine,
  vintage,
  format,
  network,
  markets,
  competitor,
  competitorVintage
}: {
  data: components['schemas']['DataDetail'][];
  wine: string | null;
  vintage: string | null;
  format: string | null;
  network: string | null;
  markets: string[];
  competitor: string | null;
  competitorVintage?: string | null;
}) => {
  const wineData = applyFiltersToData({
    data,
    wine,
    vintage,
    format,
    network,
    markets
  });
  const competitorData = competitor
    ? [
        applyFiltersToData({
          data,
          wine: competitor,
          vintage: competitorVintage ? competitorVintage : vintage,
          format,
          network,
          markets
        })
      ]
    : [];

  return transformDataForChart({
    data: [wineData, ...competitorData]
  });
};

const averageDataEuro = (data: components['schemas']['DataDetail'][]) => {
  let dataEuro = [...data];
  dataEuro = dataEuro.filter((item) => item.local_currency === 'EUR');
  let averagedData: { [key: string]: any } = { sumFacing: 0 };
  dataEuro.forEach((item) => {
    averagedData = { ...item, ...averagedData };
    averagedData.sumFacing += item.facing;
    if (item.min_price && item.min_price < averagedData.min_price) {
      averagedData.min_store = item.min_store;
      averagedData.min_price = item.min_price;
      averagedData.min_local_price = item.min_local_price;
    }
    if (item.max_price && item.max_price > averagedData.max_price) {
      averagedData.max_store = item.max_store;
      averagedData.max_price = item.max_price;
      averagedData.max_local_price = item.max_local_price;
    }

    Object.keys(item).forEach((key: string) => {
      if (key.includes('avg')) {
        averagedData[key] =
          (averagedData[key] * (averagedData.sumFacing - item.facing) +
            // @ts-expect-error dynamic key
            item[key] * item.facing) /
          averagedData.sumFacing;
      }
    });
  });
  averagedData.market_name = I18n.t('euroCountries');
  return averagedData;
};

const applyFiltersToData = ({
  data,
  wine,
  vintage,
  format,
  network,
  markets
}: {
  data: components['schemas']['DataDetail'][];
  wine: string | null;
  vintage: string | null;
  format: string | null;
  network: string | null;
  markets: string[];
}) => {
  let filteredData = dataFilter(
    data,
    getDataFilters({ vintage, wine, format, network })
  );
  filteredData.unshift(averageDataEuro(filteredData));
  filteredData = filterDataByMarket({ data: filteredData, markets });
  return filteredData;
};

const transformDataForChart = ({ data }: { data: any[] }) => {
  const transformItemFunction = (item: any) => {
    return {
      ...item,
      name: item.market_name,
      minPrice: {
        icon: 'negative',
        currency: item.displayed_currency,
        value: item.min_price
      },
      value: {
        icon: 'neutral',
        currency: item.displayed_currency,
        value: item.avg_price
      },
      maxPrice: {
        icon: 'positive',
        currency: item.displayed_currency,
        value: item.max_price
      }
    };
  };

  // Create formatted data
  const formattedData: any[] = data.map((set: any) =>
    set.map((item: any) => transformItemFunction(item))
  );

  // Sort formatted data by names
  formattedData.forEach((set) =>
    set.sort((a: any, b: any) =>
      String(a.name).toLowerCase().localeCompare(String(b.name).toLowerCase())
    )
  );

  // Create unique sorted list of names
  const setNames = [
    ...new Set(
      formattedData.flatMap((set) => set.map((item: any) => item.name))
    )
  ].sort((a, b) =>
    String(a).toLowerCase().localeCompare(String(b).toLowerCase())
  );

  // Create list of wines (index of wine name is the same as index of set)
  const wines: string | null[] = formattedData
    .map((set) => (set && set.length > 0 ? set[0].wine_name : null))
    .filter((item) => item);

  // Fill missing values (prices) with 0 values
  formattedData.forEach((set, setIndex) => {
    setNames.forEach((name, index) => {
      if (!set[index] || set[index].name !== name) {
        set.splice(index, 0, {
          name,
          value: 0,
          wine_name: wines[setIndex]
        });
      }
    });
  });
  return formattedData;
};

export { getDataFilters, getChartData };
